import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createMigrate,
  MigrationManifest,
  PersistedState,
  persistReducer,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

export interface IClinicStateSlice {
  error: unknown;
  clinicState: IReduxLoadingState;
  clinicList: IClinic[];
  userClinic?: IClinic;
  clinicFloorList: IClinicFloor[];
  loadingFloors: number[];
  selectedFloors: null | number[];
  redirectToUserInfo: boolean;
}

const name = "clinic";

export const initialState: IClinicStateSlice = {
  clinicState: "pending",
  error: "",
  clinicList: [],
  userClinic: undefined,
  clinicFloorList: [],
  loadingFloors: [],
  selectedFloors: null,
  redirectToUserInfo: false,
};

export const clinicSlice = createSlice({
  name,
  initialState,
  reducers: {
    setUserClinic(state, action: PayloadAction<{ clinic: IClinic }>) {
      state.userClinic = action.payload.clinic;
    },
    setRedirectToUserInfo(state, action: PayloadAction<boolean>) {
      state.redirectToUserInfo = action.payload;
    },
    setClinicList(state, action: PayloadAction<{ clinicList: IClinic[] }>) {
      state.clinicList = action.payload.clinicList;
    },
    setClinicFloorList(
      state,
      action: PayloadAction<{ clinicFloorList: IClinicFloor[] }>
    ) {
      state.clinicFloorList = action.payload.clinicFloorList;
      return state;
    },
    setSelectedFloors(state, action: PayloadAction<number[]>) {
      state.selectedFloors = action.payload;
    },
    setClinicState: (
      state,
      action: PayloadAction<Partial<IClinicStateSlice>>
    ) => ({
      ...state,
      ...action.payload,
    }),
    setClinicSliceReset() {
      return initialState;
    },
  },
  extraReducers() {},
});

export const {
  setUserClinic,
  setClinicList,
  setClinicFloorList,
  setSelectedFloors,
  setClinicState,
  setClinicSliceReset,
  setRedirectToUserInfo,
} = clinicSlice.actions;

// Persisted-cache migrations. Bump `version` (below) whenever previously
// persisted clinic data must be invalidated for all users on their next load.
//
// v1: drop the cached `clinicList` so it is refetched with the newly-added
// `clinics.status` field. Clients that persisted a pre-`status` clinicList would
// otherwise rehydrate it from disk, and the Neo `status === 2` (live-facility)
// filter would match zero clinics until a manual cache clear. The boot path
// (appRouter → populateAppData → getMyClinics) repopulates the emptied list.
const migrations: MigrationManifest = {
  1: (state) => {
    if (!state) return state;
    return { ...state, clinicList: [] } as unknown as PersistedState;
  },
};

const persistedReducer = persistReducer(
  {
    key: name,
    storage: storage,
    version: 1,
    migrate: createMigrate(migrations, { debug: false }),
    whitelist: ["selectedFloors", "clinicList", "userClinic"],
  },
  clinicSlice.reducer
);

export default persistedReducer;
